import { useMemo, useState, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage as ChatMessageType } from '@/context/ChatContext';
import { apiClient } from '@/api/client';
import { tryParseAriaResponse } from '@/utils/ariaResponse';

interface Props {
  message: ChatMessageType;
  onChipClick?: (text: string) => void;
}

/** Normalize escaped newlines and strip kramdown-style attributes */
function normalizeMarkdown(md: string): string {
  return md
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\)\{[^}]*width\s*=\s*\d+[^}]*\}/g, ')');  // strip { width=300 } after images
}

export function ChatMessageBubble({ message, onChipClick }: Props) {
  const isUser = message.role === 'user';

  const { markdown, followups } = useMemo(() => {
    if (isUser) return { markdown: message.content, followups: [] as string[] };
    const parsed = tryParseAriaResponse(message.content);
    if (parsed) return { markdown: normalizeMarkdown(parsed.markdown), followups: parsed.followups ?? [] };
    return { markdown: normalizeMarkdown(message.content), followups: [] as string[] };
  }, [message.content, isUser]);

  // User messages: right-aligned bubble, compact
  if (isUser) {
    const hasAttachments = (message.attachments && message.attachments.length > 0) || (message.localPreviews && message.localPreviews.length > 0);
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[80%] rounded-2xl px-4 py-2.5 bg-purple-600 text-white">
          {hasAttachments && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {message.localPreviews?.map((p, i) => (
                <AttachmentPill key={`local-${i}`} fileName={p.fileName} mimeType={p.mimeType} dataUrl={p.dataUrl} />
              ))}
              {message.attachments?.map(a => (
                <AttachmentPill key={a.id} fileName={a.fileName} mimeType={a.mimeType} attachmentId={a.id} />
              ))}
            </div>
          )}
          <p className="text-sm whitespace-pre-wrap">{markdown}</p>
        </div>
      </div>
    );
  }

  // ARIA messages: full width, no bubble constraint
  return (
    <div className="mb-4">
      {/* Source badge for mobile messages */}
      {message.source === 'mobile' && (
        <div className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">from mobile app</div>
      )}

      {/* Message content — full width */}
      <div className="rounded-2xl px-4 py-3 bg-gray-800/60 text-gray-100">
        <div className="aria-markdown">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              table: ({ children }) => (
                <div className="overflow-x-auto" role="region" aria-label="ARIA table" tabIndex={0}>
                  <table>{children}</table>
                </div>
              ),
              img: ({ src, alt }) => {
                const [broken, setBroken] = useState(false);
                if (broken) return null;
                const ytMatch = src?.match(/ytimg\.com\/vi\/([^/]+)\//);
                const imgEl = (
                  <img
                    src={src}
                    alt={alt || ''}
                    className="rounded-lg my-2"
                    style={{ maxWidth: '300px', width: '100%', height: 'auto' }}
                    loading="lazy"
                    onError={() => setBroken(true)}
                  />
                );
                if (ytMatch) {
                  return (
                    <a href={`https://www.youtube.com/watch?v=${ytMatch[1]}`} target="_blank" rel="noopener noreferrer" className="block">
                      {imgEl}
                      <span className="text-xs text-purple-400 hover:text-purple-300">🎬 Watch on YouTube</span>
                    </a>
                  );
                }
                return imgEl;
              },
            }}
          >{markdown}</ReactMarkdown>
        </div>

        {/* Streaming indicator */}
        {message.isStreaming && !message.content && (
          <div className="flex gap-1 py-2">
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        )}

        {/* Follow-up chips */}
        {followups.length > 0 && !message.isStreaming && (
          <div className="flex flex-wrap gap-1.5 mt-3 pt-2 border-t border-gray-700/50">
            {followups.map((chip, i) => (
              <button
                key={i}
                onClick={() => onChipClick?.(chip)}
                className="text-xs px-3 py-1.5 rounded-full bg-purple-500/20 text-purple-300
                  hover:bg-purple-500/30 transition-colors cursor-pointer"
              >
                {chip}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentPill({ fileName, mimeType, attachmentId, dataUrl }: {
  fileName: string; mimeType: string; attachmentId?: string; dataUrl?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(dataUrl);
  const isImage = mimeType.startsWith('image/');

  useEffect(() => {
    if (isImage && !dataUrl && attachmentId) {
      apiClient.getChatAttachmentUrl(attachmentId).then(setResolvedUrl).catch(() => {});
    }
  }, [isImage, dataUrl, attachmentId]);

  const handleClick = useCallback(async () => {
    if (isImage) { setExpanded(e => !e); return; }
    if (attachmentId) {
      const url = await apiClient.getChatAttachmentUrl(attachmentId);
      window.open(url, '_blank');
    }
  }, [isImage, attachmentId]);

  const imgSrc = dataUrl || resolvedUrl;

  return (
    <div className="cursor-pointer" onClick={handleClick}>
      {isImage && imgSrc && (
        <img src={imgSrc} alt={fileName}
          className={`rounded ${expanded ? 'max-w-full' : 'w-16 h-16 object-cover'}`} />
      )}
      {isImage && !imgSrc && (
        <div className="flex items-center gap-1 text-xs text-purple-200 bg-purple-700/50 px-2 py-1 rounded animate-pulse">
          <span>{fileName}</span>
        </div>
      )}
      {!isImage && (
        <div className="flex items-center gap-1 text-xs text-purple-200 bg-purple-700/50 px-2 py-1 rounded">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>{fileName}</span>
        </div>
      )}
    </div>
  );
}
